import type { FC, ReactNode } from 'react'

import { useRef } from 'react'

import { gsap, useGsap, prefersReducedMotion } from '@/lib/gsap'
import { Rule } from '@/components/generals/rule'
import { Reveal } from '@/components/generals/reveal'
import { SplitText } from '@/components/generals/split-text'
import { ArrowLink } from '@/components/generals/arrow-link'
import { SectionLabel } from '@/components/generals/section-label'
import { ArticleRow } from '@/components/journal/article-card'
import { RowPreview } from '@/components/journal/row-preview'
import { articles } from '@/constants/journal'

const featured = articles.slice(0, 4)

export const StudioJournal: FC = (): ReactNode => {
    const scope = useRef<HTMLElement>(null)

    useGsap(() => {
        const root = scope.current

        if (!root || prefersReducedMotion()) {
            return
        }

        gsap.fromTo('[data-journal-rule]',
            { scaleX: 0 },
            {
                scaleX: 1,
                duration: 1.15,
                stagger: 0.07,
                transformOrigin: 'left',
                ease: 'noma',
                scrollTrigger: { trigger: '[data-studio-feed]', start: 'top 88%', once: true }
            }
        )

        gsap.fromTo('[data-journal-row] a',
            { opacity: 0, y: 28 },
            {
                opacity: 1,
                y: 0,
                duration: 1,
                stagger: 0.07,
                ease: 'noma',
                scrollTrigger: { trigger: '[data-studio-feed]', start: 'top 88%', once: true }
            }
        )
    }, scope, [])

    return (
        <section
            ref={scope}
            id='studio-journal'
            aria-labelledby='studio-journal-title'
            className='relative z-10 bg-paper text-ink'
        >
            <div className='mx-auto flex max-w-[1920px] flex-col gap-8 px-5 sec-b lg:gap-10 lg:px-[33px]'>
                <Rule />

                <div className='grid grid-cols-1 items-end gap-6 lg:grid-cols-4 lg:gap-5'>
                    <SectionLabel label='From the journal' className='h-fit' />

                    <SplitText
                        as='h2'
                        id='studio-journal-title'
                        text='articles.'
                        stagger={0.03}
                        className='t-display text-ink lg:col-span-2'
                        style={{ fontSize: 'clamp(45px, 6vw, 80px)' }}
                    />

                    <Reveal
                        from='up'
                        distance={20}
                        delay={0.15}
                        className='flex justify-start lg:justify-end'
                    >
                        <ArrowLink href='/journal' label='View All' />
                    </Reveal>
                </div>

                <ul data-studio-feed className='flex flex-col'>
                    {featured.map((article, index) => (
                        <ArticleRow key={article.slug} article={article} index={index} />
                    ))}

                    <span
                        data-journal-rule
                        aria-hidden='true'
                        className='block h-px w-full origin-left bg-current opacity-15'
                    />
                </ul>

                <RowPreview />
            </div>
        </section>
    )
}
